import { useEffect, useState, type FormEvent } from 'react';
import {
  api,
  BladApi,
  type Kontrahent,
  type PozycjaSlownika,
  type TrybZlecenia,
  type UrzadzenieWiersz,
} from '../api';
import { useIdOperacji } from '../operacje';
import { useUstawienia } from '../ustawienia';
import { idz } from '../router';
import { IkonaWstecz } from '../components/Icons';
import { NaglowekWidoku } from '../components/Shell';
import { WyborKategorii } from '../components/WyborKategorii';
import { WyborKontrahenta } from '../components/WyborKontrahenta';
import {
  WyborZdjec,
  ZDJECIA_KARTY,
  wyslijZdjecia,
  type ZdjecieWFormularzu,
} from '../components/Zdjecia';
import { Karta, Komunikat, PoleTekstowe, Przycisk, PrzyciskMarki } from '../components/ui';

/**
 * Przyjęcie zgłoszenia serwisowego.
 *
 * **Trzy pola i koniec**: klient, maszyna, opis usterki (CLAUDE.md §1 zasada 2).
 * Ten sam formularz wypełnia konsultant przy telefonie i serwisant w terenie —
 * jeden formularz, jedna baza, żadnych rozbieżności.
 *
 * Maszynę wybiera się z listy, a nie wpisuje: to ona przynosi klienta, numer boczny
 * i całą historię, więc biuro nie musi dopytywać, „która to maszyna".
 */
export function NewTicketView() {
  const operacja = useIdOperacji();

  const [urzadzenie, setUrzadzenie] = useState<UrzadzenieWiersz | null>(null);
  const [opisUsterki, setOpisUsterki] = useState('');
  const [objawy, setObjawy] = useState<string[]>([]);
  /** Ostatni opis wstawiony z objawów — po nim poznajemy, czy wolno go zmienić. */
  const [opisZObjawow, setOpisZObjawow] = useState('');
  const [tryb, setTryb] = useState<TrybZlecenia>('WYJAZD');
  const [priorytet, setPriorytet] = useState('normalny');
  const [wyposazenie, setWyposazenie] = useState('');
  const [polka, setPolka] = useState('');
  const [slownik, setSlownik] = useState<PozycjaSlownika[]>([]);
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  useEffect(() => {
    api
      .slownikiSerwisu()
      .then((wynik) => setSlownik(wynik.objawy))
      .catch(() => setSlownik([]));
  }, []);

  const gotowe = urzadzenie && opisUsterki.trim();

  /**
   * Kliknięcie objawu wpisuje jego nazwę do opisu usterki.
   *
   * Zasada 8: pole, które da się wypełnić za użytkownika, jest wypełnione. Wcześniej
   * kliknięcie „Gubi ścieg" nie robiło nic dla zapisu — trzeba było jeszcze wpisać to
   * samo ręcznie, inaczej zgłoszenia nie dało się przyjąć. Opis pozostaje wymagany
   * (zasada 2), ale nie każemy przepisywać tego, co już wskazano palcem.
   *
   * Nadpisujemy wyłącznie własną poprzednią podpowiedź. Tekst napisany ręcznie jest
   * nietykalny — inaczej zdanie od klienta znikałoby przy jednym kliknięciu.
   */
  function przelaczObjaw(kod: string): void {
    const nowe = objawy.includes(kod) ? objawy.filter((o) => o !== kod) : [...objawy, kod];
    setObjawy(nowe);

    const tekst = slownik
      .filter((objaw) => nowe.includes(objaw.kod))
      .map((objaw) => objaw.nazwa)
      .join(', ');

    if (opisUsterki.trim() === '' || opisUsterki === opisZObjawow) {
      setOpisUsterki(tekst);
      setOpisZObjawow(tekst);
    }
  }

  async function wyslij(zdarzenie: FormEvent): Promise<void> {
    zdarzenie.preventDefault();
    if (!gotowe || zapis) return;

    setZapis(true);
    setBlad(null);
    try {
      const karta = await api.utworzZlecenie({
        clientOperationId: operacja.biezacy(),
        customerId: urzadzenie.customer.id,
        deviceId: urzadzenie.id,
        opisUsterki: opisUsterki.trim(),
        objawy,
        tryb,
        priorytet,
        wyposazenie: tryb === 'WARSZTAT' ? wyposazenie.trim() || undefined : undefined,
        polka: tryb === 'WARSZTAT' ? polka.trim() || undefined : undefined,
      });
      operacja.zakoncz();
      idz(`/zlecenia/${karta.zlecenie.id}`);
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się przyjąć zgłoszenia.');
      setZapis(false);
    }
  }

  return (
    <>
      <NaglowekWidoku
        tytul="Nowe zgłoszenie"
        podtytul="Ten sam formularz, co na telefonie serwisanta"
        akcje={
          <Przycisk
            type="button"
            onClick={() => idz('/zlecenia')}
            ikona={<IkonaWstecz rozmiar={16} />}
          >
            Anuluj
          </Przycisk>
        }
      />

      <form onSubmit={wyslij} className="max-w-[760px]" noValidate>
        <Karta
          tytul="Wymagane — trzy pola"
          wyroznienie
          akcje={<span className="t-small text-n-600">numer nada system</span>}
        >
          <div className="mb-14">
            <WyborUrzadzenia wybrane={urzadzenie} onWybor={setUrzadzenie} />
          </div>

          <label className="block">
            <span className="t-label mb-5 block text-n-700">
              Opis zgłoszonej usterki
              <span className="t-small text-n-500"> — wg informacji od klienta</span>
            </span>
            <textarea
              value={opisUsterki}
              onChange={(z) => setOpisUsterki(z.target.value)}
              rows={3}
              placeholder="np. Gubi ścieg przy grubszych materiałach, słychać stukanie."
              className="w-full border border-n-300 bg-surface px-13 py-9 text-n-900"
            />
          </label>
        </Karta>

        <Karta
          tytul="Objawy — kliknij, nie pisz"
          akcje={<span className="t-small text-n-500">wpisują się do opisu usterki</span>}
        >
          <div className="flex flex-wrap gap-7">
            {slownik.map((objaw) => {
              const wybrany = objawy.includes(objaw.kod);
              return (
                <button
                  key={objaw.kod}
                  type="button"
                  aria-pressed={wybrany}
                  onClick={() => przelaczObjaw(objaw.kod)}
                  className={`border px-13 ${
                    wybrany
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-n-300 bg-surface text-n-700 hover:bg-n-50'
                  }`}
                >
                  <span className="t-label">{objaw.nazwa}</span>
                </button>
              );
            })}
          </div>
        </Karta>

        <Karta tytul="Uzupełnienia" akcje={<span className="t-small text-n-500">wszystko opcjonalne</span>}>
          <div className="grid gap-13 sm:grid-cols-2">
            <label className="block">
              <span className="t-label mb-5 block text-n-700">Tryb</span>
              <select
                value={tryb}
                onChange={(z) => setTryb(z.target.value as TrybZlecenia)}
                className="w-full border border-n-300 bg-surface px-13 text-n-900"
              >
                <option value="WYJAZD">Wyjazd do klienta</option>
                <option value="WARSZTAT">Warsztat</option>
              </select>
            </label>

            <label className="block">
              <span className="t-label mb-5 block text-n-700">Priorytet</span>
              <select
                value={priorytet}
                onChange={(z) => setPriorytet(z.target.value)}
                className="w-full border border-n-300 bg-surface px-13 text-n-900"
              >
                <option value="normalny">normalny</option>
                <option value="pilny">pilny — linia stoi</option>
              </select>
            </label>

            {/* Pola warsztatowe pojawiają się dopiero, gdy maszyna do nas przyjeżdża —
                przy wyjeździe do klienta byłyby pytaniem bez sensu. */}
            {tryb === 'WARSZTAT' && (
              <>
                <PoleTekstowe
                  etykieta="Przyjęte wyposażenie"
                  podpowiedz="co przyjechało razem z maszyną"
                  value={wyposazenie}
                  onChange={(z) => setWyposazenie(z.target.value)}
                  autoComplete="off"
                />
                <PoleTekstowe
                  etykieta="Lokalizacja w warsztacie"
                  podpowiedz="np. regał B, półka 3"
                  value={polka}
                  onChange={(z) => setPolka(z.target.value)}
                  autoComplete="off"
                />
              </>
            )}
          </div>
        </Karta>

        {blad && (
          <div className="mb-14">
            <Komunikat tresc={blad} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-13">
          <PrzyciskMarki type="submit" disabled={!gotowe || zapis}>
            {zapis ? 'Przyjmowanie…' : 'Przyjmij zgłoszenie'}
          </PrzyciskMarki>
          <span className="t-small text-n-500">
            Serwisanta i termin przypiszesz na karcie zlecenia.
          </span>
        </div>
      </form>
    </>
  );
}

/**
 * Wybór maszyny, a nie klienta: maszyna przynosi ze sobą klienta, numer boczny
 * i historię. Szukanie obsługuje numer boczny, fabryczny, model i nazwę klienta —
 * czyli wszystko, co człowiek może mieć przed oczami.
 */
function WyborUrzadzenia({
  wybrane,
  onWybor,
}: {
  wybrane: UrzadzenieWiersz | null;
  onWybor: (urzadzenie: UrzadzenieWiersz | null) => void;
}) {
  const [szukane, setSzukane] = useState('');
  const [wyniki, setWyniki] = useState<UrzadzenieWiersz[]>([]);
  const [zakladanie, setZakladanie] = useState(false);

  useEffect(() => {
    if (wybrane) return;
    const fraza = szukane.trim();
    if (fraza.length < 2) {
      setWyniki([]);
      return;
    }

    const zegar = window.setTimeout(() => {
      api
        .urzadzenia({ q: fraza, limit: 8 })
        .then((wynik) => setWyniki(wynik.urzadzenia))
        .catch(() => setWyniki([]));
    }, 250);

    return () => window.clearTimeout(zegar);
  }, [szukane, wybrane]);

  if (wybrane) {
    return (
      <div>
        <span className="t-label mb-5 block text-n-700">Maszyna</span>
        <div className="flex min-h-44 flex-wrap items-center gap-13 border border-n-300 bg-surface px-13 py-5">
          <span className="mr-auto">
            <span className="t-body-strong block text-n-900">
              {wybrane.producent} {wybrane.model}
            </span>
            <span className="t-small block text-n-600">
              {wybrane.customer.nazwa} · nr boczny {wybrane.sideNumber}
            </span>
          </span>
          <Przycisk type="button" onClick={() => onWybor(null)}>
            Zmień
          </Przycisk>
        </div>
      </div>
    );
  }

  // Pierwsze przyjęcie maszyny do serwisu: karty jeszcze nie ma, bo baza urządzeń
  // startuje pusta i rośnie z montaży oraz przyjęć (CLAUDE.md §9.3). Zakładamy ją
  // TUTAJ, bez opuszczania zgłoszenia — odesłanie do osobnego formularza kasowało
  // wypełniony opis usterki i łamało zasadę 4: dane mają narastać, nie blokować.
  if (zakladanie) {
    return (
      <SzybkaKarta
        nazwaWstepna={szukane.trim()}
        onZalozona={(urzadzenie) => {
          setZakladanie(false);
          onWybor(urzadzenie);
        }}
        onAnuluj={() => setZakladanie(false)}
      />
    );
  }

  return (
    <div>
      <PoleTekstowe
        etykieta="Maszyna"
        podpowiedz="numer boczny, fabryczny, model albo nazwa klienta"
        value={szukane}
        onChange={(z) => setSzukane(z.target.value)}
        autoComplete="off"
      />

      {wyniki.length > 0 && (
        <div className="mt-5 border border-n-100 bg-surface">
          {wyniki.map((urzadzenie) => (
            <button
              key={urzadzenie.id}
              type="button"
              onClick={() => onWybor(urzadzenie)}
              className="flex w-full flex-col items-start border-0 border-b border-n-100 bg-surface px-13 py-9 text-left last:border-b-0 hover:bg-n-50"
            >
              <span className="t-body text-n-900">
                {urzadzenie.producent} {urzadzenie.model}
              </span>
              <span className="t-small text-n-600">
                {urzadzenie.customer.nazwa} · <span className="t-mono-data">{urzadzenie.sideNumber}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Przycisk jest widoczny zawsze, nie dopiero po nieudanym szukaniu. Przy
          pierwszym przyjęciu maszyny nie ma czego szukać, a zgadywanie, że trzeba
          najpierw wpisać cokolwiek w pole obok, było zagadką bez podpowiedzi. */}
      <div className="mt-9 flex flex-wrap items-center gap-13">
        {wyniki.length === 0 && szukane.trim().length >= 2 && (
          <span className="t-small text-n-600">Nic nie pasuje.</span>
        )}
        <Przycisk type="button" onClick={() => setZakladanie(true)}>
          Pierwsze przyjęcie — załóż kartę
        </Przycisk>
        <span className="t-small text-n-500">bez opuszczania zgłoszenia</span>
      </div>
    </div>
  );
}

/**
 * Zakładanie karty urządzenia wewnątrz zgłoszenia — te same pola wymagane, co na
 * ekranie „Nowa karta" (klient, numer fabryczny, kategoria, producent, model),
 * bo to jedna i ta sama karta. Reszty nikt tu nie wypełnia: uzupełni się przy
 * kolejnych wizytach (zasada 4).
 */
function SzybkaKarta({
  nazwaWstepna,
  onZalozona,
  onAnuluj,
}: {
  nazwaWstepna: string;
  onZalozona: (urzadzenie: UrzadzenieWiersz) => void;
  onAnuluj: () => void;
}) {
  const operacja = useIdOperacji();
  const ustawienia = useUstawienia();

  const [klient, setKlient] = useState<Kontrahent | null>(null);
  const [zdjecia, setZdjecia] = useState<ZdjecieWFormularzu[]>([]);
  const [serialNumber, setSerialNumber] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [producent, setProducent] = useState('');
  const [model, setModel] = useState(nazwaWstepna);
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  const brakZdjec =
    ustawienia.zdjeciaWymaganePrzyKarcie &&
    ZDJECIA_KARTY.some((kind) => !zdjecia.some((z) => z.kind === kind));

  const gotowe =
    klient &&
    serialNumber.trim() &&
    categoryId &&
    producent.trim() &&
    model.trim() &&
    !brakZdjec;

  async function zaloz(): Promise<void> {
    if (!gotowe || zapis) return;

    setZapis(true);
    setBlad(null);
    try {
      const karta = await api.utworzUrzadzenie({
        clientOperationId: operacja.biezacy(),
        customerId: klient.id,
        categoryId,
        serialNumber: serialNumber.trim(),
        producent: producent.trim(),
        model: model.trim(),
      });
      operacja.zakoncz();
      await wyslijZdjecia('DEVICE', karta.urzadzenie.id, zdjecia);
      // `Urzadzenie` ma wszystko, czego potrzebuje wiersz listy, poza licznikiem braków.
      onZalozona({ ...karta.urzadzenie, braki: karta.braki.length });
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się założyć karty.');
      setZapis(false);
    }
  }

  return (
    // Wyróżnienie szarością, tak jak `Karta wyroznienie` — kolor marki ma w widoku
    // dwa zadania i żadnego więcej: jedna akcja główna i oznaczenie wyboru (§5).
    // Panel nie jest ani jednym, ani drugim.
    <div className="border border-n-300 bg-n-50 p-13">
      <div className="mb-13 flex flex-wrap items-baseline gap-9">
        <span className="t-body-strong text-n-900">Pierwsze przyjęcie maszyny</span>
        <span className="t-small text-n-600">karta powstanie razem ze zgłoszeniem</span>
      </div>

      <div className="mb-13">
        <WyborKontrahenta wybrany={klient} onWybor={setKlient} />
      </div>

      <div className="mb-13 grid gap-13 sm:grid-cols-2">
        <div>
          <PoleTekstowe
            etykieta="Numer fabryczny"
            value={serialNumber}
            onChange={(z) => setSerialNumber(z.target.value)}
            autoComplete="off"
          />
          <div className="mt-5">
            <Przycisk type="button" onClick={() => setSerialNumber('brak')}>
              Tabliczka nieczytelna — wpisz „brak"
            </Przycisk>
          </div>
        </div>
        <WyborKategorii wartosc={categoryId} onZmiana={setCategoryId} />
      </div>

      <div className="mb-13 grid gap-13 sm:grid-cols-2">
        <PoleTekstowe
          etykieta="Producent"
          value={producent}
          onChange={(z) => setProducent(z.target.value)}
          autoComplete="off"
        />
        <PoleTekstowe
          etykieta="Model"
          value={model}
          onChange={(z) => setModel(z.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="mb-13">
        <span className="t-label mb-5 block text-n-700">
          Zdjęcia
          <span className="t-small text-n-500">
            {ustawienia.zdjeciaWymaganePrzyKarcie ? ' — wymagane' : ' — zalecane'}
          </span>
        </span>
        <WyborZdjec
          zdjecia={zdjecia}
          onZmiana={setZdjecia}
          rodzaje={ZDJECIA_KARTY}
          wymagane={ustawienia.zdjeciaWymaganePrzyKarcie}
        />
      </div>

      {blad && (
        <div className="mb-13">
          <Komunikat tresc={blad} />
        </div>
      )}

      {/* Akcją główną widoku zostaje „Przyjmij zgłoszenie" — to po nią tu przyszedł
          użytkownik. Założenie karty jest krokiem po drodze, więc przycisk jest szary. */}
      <div className="flex flex-wrap items-center gap-13">
        <Przycisk type="button" onClick={() => void zaloz()} disabled={!gotowe || zapis}>
          {zapis ? 'Zakładanie…' : 'Załóż kartę i wróć do zgłoszenia'}
        </Przycisk>
        <Przycisk type="button" onClick={onAnuluj} disabled={zapis}>
          Anuluj
        </Przycisk>
        {!gotowe && (
          <span className="t-small text-n-500">
            {brakZdjec
              ? 'Firma wymaga zdjęcia ogólnego i tabliczki znamionowej.'
              : 'Potrzebne: klient, numer fabryczny, kategoria, producent i model.'}
          </span>
        )}
      </div>
    </div>
  );
}
